import type { ReactNode } from "react";

// Stonky, at the head of his desk — the character art plus an executive-facing
// brief: how the firm operates, how it's different, and the principles under it.
export default function StonkyPortrait() {
  return (
    <div className="card p-0 overflow-hidden border-gold/30 relative">
      <div className="flex flex-col lg:flex-row items-stretch">
        <div className="relative lg:w-[300px] flex-shrink-0 bg-ink">
          {/* Mobile: show the WHOLE character (no crop) so the face + cigar +
              whiskey are never cut. Desktop: the tall side-panel crop. */}
          <img
            src="/assets/stonky.jpg"
            alt="Stonky — the firm's ruthless robot trader, cigar in mouth, whiskey and cash in hand"
            className="w-full h-auto object-contain lg:h-full lg:object-cover lg:object-top"
            loading="eager"
          />
          <div className="absolute inset-0 pointer-events-none hidden lg:block"
               style={{ boxShadow: "inset -40px 0 60px -30px rgba(10,10,12,0.9)" }} />
        </div>

        <div className="flex-1 p-5 flex flex-col justify-center">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-2xl font-black tracking-tight text-slate-100">STONKY</span>
            <span className="text-[11px] uppercase tracking-[0.22em] text-gold/80">
              Ruthless · Disciplined · The final say
            </span>
          </div>
          <p className="text-sm text-slate-300 mt-1 mb-4 max-w-3xl leading-snug">
            A <span className="text-slate-100 font-semibold">governed AI trading firm</span> — a board and
            desk of <span className="text-slate-100 font-semibold">specialized AI agents</span>, run like an
            institution, not a bot. A board deliberates, Stonky ratifies the day&rsquo;s mandate, and the
            desk executes it under a risk code no agent can override.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <Pillar title="How it operates" tone="text-accent">
              <li>A board convenes each session — macro, an 11-analyst sector desk, market structure, and a performance officer.</li>
              <li>Stonky, the principal, ratifies or vetoes the day&rsquo;s directives.</li>
              <li>Junior strategists propose trades; each clears peer review and a deterministic risk gate before a dollar moves.</li>
              <li>Every night it writes a post-mortem that sharpens the next session.</li>
            </Pillar>
            <Pillar title="How it&rsquo;s different" tone="text-gold">
              <li><span className="text-slate-200">A firm of AI agents</span> — a board and desk of purpose-built models that debate and check each other, not one black-box.</li>
              <li><span className="text-slate-200">Governed</span> — a written Constitution and hard-coded risk limits bind every agent, Stonky included.</li>
              <li><span className="text-slate-200">Evidence, not vibes</span> — corroborated across 30+ global outlets and 11 sector specialists.</li>
              <li><span className="text-slate-200">Discipline over activity</span> — it stands down with no edge, and never hides a loss.</li>
            </Pillar>
            <Pillar title="What grounds it" tone="text-up">
              <li>Process over outcome.</li>
              <li>Every position defines its exit before entry.</li>
              <li>Flat by the close — no overnight risk on the intraday book.</li>
              <li>Corroboration or it&rsquo;s noise.</li>
              <li>Fully traceable — every trade back to its cause.</li>
            </Pillar>
          </div>

          <div className="mt-4 rounded-lg border border-accent/30 bg-accent/5 p-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-accent mb-1">
              A memory that compounds — on fock.space
            </div>
            <p className="text-xs text-slate-400 leading-snug">
              Every decision is written to the firm&rsquo;s <span className="text-slate-200">institutional knowledge graph</span> —
              each trade wired back to the directive, evidence, and board session that caused it. That graph is
              mirrored to <span className="text-slate-200">fock.space</span> as private, end-to-end-encrypted memory the
              firm carries forward and links into a broader universe of ideas. The firm doesn&rsquo;t just trade —
              it <span className="text-slate-200">remembers, learns, and compounds what it knows</span>.
            </p>
          </div>

          <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-up animate-pulse" />
            at the table
          </div>
        </div>
      </div>
    </div>
  );
}

function Pillar({ title, tone, children }: { title: string; tone: string; children: ReactNode }) {
  return (
    <div>
      <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1.5 ${tone}`}
           dangerouslySetInnerHTML={{ __html: title }} />
      <ul className="space-y-1 text-xs text-slate-400 leading-snug list-disc pl-4">
        {children}
      </ul>
    </div>
  );
}
